"use client"

import * as React from "react"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card"
import { User, MessageCircle, Calendar, Clock } from "lucide-react"

interface UserData {
  id?: string
  name: string
  profilePictureUrl?: string
  avatar?: string
  role?: string
  joinDate?: string
  createdAt?: string
  lastActive?: string
  email?: string
}

interface UserHoverCardProps {
  user: UserData
  children: React.ReactNode
  showActions?: boolean
}

export function UserHoverCard({ user, children, showActions = true }: UserHoverCardProps) {
  const profileUrl = user.profilePictureUrl || user.avatar
  const joinDate = user.joinDate || user.createdAt
  const lastActive = user.lastActive

  const formatDate = (dateString?: string) => {
    if (!dateString) return null
    return new Date(dateString).toLocaleDateString()
  }

  const getRoleBadge = (role?: string) => {
    if (!role) return null
    const roleColors: Record<string, string> = {
      pastor: "bg-primary/10 text-primary dark:bg-primary/20",
      admin: "bg-destructive/10 text-destructive dark:bg-destructive/20",
      media_team: "bg-success/10 text-success dark:bg-success/20",
      member: "bg-muted text-muted-foreground",
    }
    return (
      <Badge className={`text-xs capitalize ${roleColors[role] || "bg-muted text-muted-foreground"}`}>
        {role.replace('_', ' ')}
      </Badge>
    )
  }

  return (
    <HoverCard>
      <HoverCardTrigger asChild>
        {children}
      </HoverCardTrigger>
      <HoverCardContent className="w-80">
        <div className="flex items-start gap-4">
          <Avatar className="h-16 w-16">
            <AvatarImage src={profileUrl} alt={user.name} />
            <AvatarFallback className="text-lg">
              {user.name.charAt(0).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 space-y-2">
            <div>
              <h4 className="font-semibold text-sm">{user.name}</h4>
              {user.role && getRoleBadge(user.role)}
            </div>
            <div className="space-y-1 text-xs text-muted-foreground">
              {joinDate && (
                <div className="flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  <span>Joined {formatDate(joinDate)}</span>
                </div>
              )}
              {lastActive && (
                <div className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  <span>Last active {formatDate(lastActive)}</span>
                </div>
              )}
            </div>
            {showActions && (
              <div className="flex gap-2 pt-2">
                <Button variant="outline" size="sm" className="h-7 text-xs">
                  <User className="h-3 w-3 mr-1" />
                  View Profile
                </Button>
                <Button variant="outline" size="sm" className="h-7 text-xs">
                  <MessageCircle className="h-3 w-3 mr-1" />
                  Message
                </Button>
              </div>
            )}
          </div>
        </div>
      </HoverCardContent>
    </HoverCard>
  )
}